import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getGlowtide, preloadGlowtide } from '../glowtide-assets.js';
import { VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La lumière naît dans l'eau.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'glowtide',
  family: 'photographic',
  danceability: 0.36,
  energyLevel: 0.25,
  bpmMin: 55,
  bpmMax: 115,
  movement: 0.25,
  tonality: 0.85,
  enabled: true
};

// GLOWTIDE : une plage la nuit. Les vagues qui se brisent s'illuminent de
// bleu bioluminescent, et l'écume lumineuse court sur le sable mouillé avant
// de s'éteindre. Ambient, downtempo, R&B lent, électronique contemplative,
// lo-fi. Règles de kiosk-app/CLAUDE.md (R1 à R17) ; danceability 0,36 : le
// preset LOUNGE de R9, aucun flash, luminance moyenne sous 0,35 (R10).
//
// LA PHOTO, ses masques (R mer, G écume, B sable mouillé) et ses LIGNES
// D'ÉCUME viennent de tools/build-glowtide.mjs (constantes mesurées dans son
// en-tête) : pour chaque ligne et chaque colonne de l'image, la hauteur de la
// crête et sa force ; puis le rivage et le bord du sable sec.
//
// LA LUMIÈRE NE VIENT PAS D'UNE SOURCE : elle naît là où l'eau est agitée.
//  - Une VAGUE est un front qui court le long d'une ligne d'écume, de gauche
//    à droite ou l'inverse, à vitesse constante. Pour chaque colonne, tau =
//    le temps écoulé depuis le passage du front : un pic au front, une
//    décroissance d'une seconde derrière lui. Ce champ ne vaut que près de la
//    ligne (une bande serrée au-dessus, une traîne vers le rivage dans l'eau
//    brisée) et il est MULTIPLIÉ par l'écume de la photo (luminance x masque
//    d'écume) : la mer calme et le ciel ont une écume nulle, ils ne
//    s'allument jamais.
//  - Puis la NAPPE : quand la lumière a rejoint le rivage (un délai selon la
//    distance de la ligne au rivage), une nappe fine monte le sable mouillé
//    (portée irrégulière par bruit le long de la plage, bord bruité) ; elle
//    s'éteint en PAILLETTES : un bruit seuillé par cellules de 2 px, qui
//    scintille et disparaît.
//  - Le sable mouillé REFLÈTE la lumière de l'écume : le point miroir de
//    l'autre côté du rivage, trois prises verticales (un miroir flou).
// Couleur : cyan bioluminescent ; la palette ne la teinte qu'à 25 %, et
// seulement vers le vert ou le bleu (sa teinte est ramenée dans [0,33 ; 0,66]).
//
// DEUX PASSES : le champ de lumière en demi-résolution (R2 : il stocke des
// INTENSITÉS - front, nappe, paillettes - lisses), puis l'affichage en pleine
// résolution qui les multiplie par l'écume de la photo, seuille les
// paillettes et lit le miroir. Les vagues (4 au plus) et les étincelles
// isolées (6) sont des uniforms SÉPARÉS, déroulés dans le GLSL : V3D lit un
// tableau d'uniforms indexé en boucle par la TMU (voir Shockwave).
//
// LE RYTHME (preset lounge) : PHRASE une vague ; DROP trois vagues sur
// 4 mesures, chacune sur une ligne plus proche du rivage ; entre deux vagues,
// de rares étincelles dans l'écume. L'énergie lissée sur 8 mesures règle
// l'intensité des vagues. Aucun flash.

const MAX_WAVES = 4;
const MAX_SPARKS = 6;
const MAX_LINES = 8;
const FOCUS = [0.5, 0.62];
const CROSS_S = 5.5;                 // a front crosses the picture in 5,5 s
const FRONT_DECAY_S = 1.0;
const INRUSH_PX_S = 90;              // the light runs from the line to the shore
const SHEET_TAIL_S = 7.0;
const DROP_BARS = 4;
const SPARK_RATE = 0.22;             // isolated sparks per second, between waves
const SPARK_S = 0.8;
const NO_BPM = 70;
const ENERGY_BARS = 8;
const SAFE_FLOOR = 0.55;
const CYAN = [0.10, 0.85, 1.0];
const DEBUG_WAVE_EVERY_S = 10;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const TIER = { 0: { light: 0.25, taps: 1 }, 1: { light: 0.5, taps: 3 }, 2: { light: 0.5, taps: 3 } };

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// The part of the image on screen ("cover", centred on FOCUS): x0, y0, w, h.
export function cropFor(imgW, imgH, aspect) {
  const ia = imgW / imgH;
  if (aspect >= ia) {
    const h = imgW / aspect;
    const y0 = Math.max(0, Math.min(imgH - h, FOCUS[1] * imgH - h / 2));
    return [0, y0, imgW, h];
  }
  const w = imgH * aspect;
  const x0 = Math.max(0, Math.min(imgW - w, FOCUS[0] * imgW - w / 2));
  return [x0, 0, w, imgH];
}

const quadVertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const COMMON = `
  uniform vec4 uCrop;        // x0, y0, w, h in image px
  uniform vec2 uImg;
  uniform sampler2D uLines;  // (K + 2) rows of W texels: y, strength
  uniform float uK;
  vec2 imgAt(vec2 uv) { return vec2(uCrop.x + uv.x * uCrop.z, uCrop.y + (1.0 - uv.y) * uCrop.w); }
  vec2 uvOfImg(vec2 img) { return vec2(img.x / uImg.x, 1.0 - img.y / uImg.y); }
  vec2 lineAt(float k, float x) { return texture2D(uLines, vec2((floor(clamp(x, 0.0, uImg.x - 1.0)) + 0.5) / uImg.x, (k + 0.5) / (uK + 2.0))).rg; }
  float h11(float n) { return fract(sin(n * 127.1 + 311.7) * 43758.5453); }
  float n1(float x) { float i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f); return mix(h11(i), h11(i + 1.0), f); }
`;

// THE LIGHT FIELD (half resolution): R = the fronts near their lines, G = the
// sheet on the wet sand, B = the sparkle density where the sheet dies.
function lightFragmentShader() {
  let waves = '';
  for (let i = 0; i < MAX_WAVES; i++) {
    waves += `
    if (uWB${i}.w > 0.5) {
      vec4 A = uWA${i}; vec4 B = uWB${i};
      vec2 ln = lineAt(A.x, img.x);
      float dy = img.y - ln.x;
      float band = dy < 0.0 ? exp(-(dy * dy) / 25.0) : exp(-dy / 22.0);
      band *= smoothstep(0.0, 3.0, shore - img.y + 2.0);
      float xf = A.y + A.z * B.y * A.w;
      float tau = A.z * (xf - img.x) / B.y;              // s since the front passed this column
      // below the crest the light rolls in toward the shore: it reaches a
      // pixel later the farther it is, so the front leans instead of cutting
      // a vertical stripe through the broken surf
      float tl = tau - max(dy, 0.0) / ${INRUSH_PX_S.toFixed(1)};
      float front = tl < 0.0 ? 0.3 * exp(tl / 0.30) : 0.55 * exp(-tl / ${FRONT_DECAY_S.toFixed(2)}) + 0.45 * exp(-tl / 0.18);
      R += B.x * band * front * max(ln.y, 0.35);
      float ts = tau - B.z;                               // s since the light reached the shore
      if (ts > 0.0 && u > -1.0) {
        float reach = depth * (0.55 + 0.40 * n1(img.x * 0.012 + A.x * 17.0 + B.z * 3.0)) * (1.0 - exp(-ts / 1.1));
        float wob = (n1(img.x * 0.03 + ts * 0.25 + A.x * 5.0) - 0.5) * 6.0 + (n1(img.x * 0.11 - ts * 0.4 + A.x * 9.0) - 0.5) * 2.0;
        float e = reach + wob - u;
        float life = exp(-max(0.0, ts - 1.6) / 1.4);
        float edge = 0.6 * exp(-(e * e) / 4.0);
        float body = e > 0.0 ? 0.22 * exp(-e / (0.5 * reach + 1.0)) : 0.0;
        G += B.x * (edge + body) * life * smoothstep(-1.0, 2.0, u);
        S += B.x * step(-1.5, e) * (1.0 - life) * exp(-max(0.0, ts - 1.6) / 3.0) * smoothstep(-1.0, 2.0, u);
      }
    }`;
  }
  let sparks = '';
  for (let i = 0; i < MAX_SPARKS; i++) {
    sparks += `
    if (uSP${i}.w > 0.0) { vec2 d = img - uSP${i}.xy; R += uSP${i}.w * exp(-dot(d, d) / 6.0); }`;
  }
  let uniforms = '';
  for (let i = 0; i < MAX_WAVES; i++) uniforms += `uniform vec4 uWA${i}; uniform vec4 uWB${i};\n`;
  for (let i = 0; i < MAX_SPARKS; i++) uniforms += `uniform vec4 uSP${i};\n`;
  return `
  precision highp float;
  ${COMMON}
  ${uniforms}
  varying vec2 vUv;
  void main() {
    vec2 img = imgAt(vUv);
    float shore = lineAt(uK, img.x).x, dry = lineAt(uK + 1.0, img.x).x;
    float u = img.y - shore, depth = max(dry - shore, 1.0);
    float R = 0.0, G = 0.0, S = 0.0;
    ${waves}
    ${sparks}
    gl_FragColor = vec4(clamp(R * 0.5, 0.0, 1.0), clamp(G, 0.0, 1.0), clamp(S, 0.0, 1.0), 1.0);
  }`;
}

function displayFragmentShader(taps) {
  const offs = taps >= 3 ? [-3, 0, 3] : [0];
  let refl = '';
  for (const o of offs) refl += `r += glowAt(uvOfImg(vec2(img.x, ym + ${o.toFixed(1)})));\n`;
  return `
  precision highp float;
  ${COMMON}
  uniform sampler2D uPhoto;
  uniform sampler2D uMasks;
  uniform sampler2D uLight;
  uniform vec3 uCol;
  uniform float uTime;
  uniform float uGain;
  uniform float uShowMask;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float lum(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
  float h21(vec2 p) { p = fract(p * vec2(0.1031, 0.1030)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
  // the lit foam at an image uv: the photo's foam (its luminance in the foam
  // mask) times the front field
  float glowAt(vec2 iuv) {
    vec2 suv = vec2((iuv.x * uImg.x - uCrop.x) / uCrop.z, 1.0 - ((1.0 - iuv.y) * uImg.y - uCrop.y) / uCrop.w);
    float f = texture2D(uMasks, iuv).g;
    return min(1.0, lum(texture2D(uPhoto, iuv).rgb) * 9.0) * smoothstep(0.04, 0.30, f) * texture2D(uLight, suv).r * 3.2;
  }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    vec2 img = imgAt(vUv);
    vec2 iuv = uvOfImg(img);
    vec3 c = texture2D(uPhoto, iuv).rgb;
    vec3 m = texture2D(uMasks, iuv).rgb;
    vec4 L = texture2D(uLight, vUv);
    // the foam gate: the faintest foam of the mask (calm water's grain) never lights
    float glow = min(1.0, lum(c) * 9.0) * smoothstep(0.04, 0.30, m.g) * L.r * 3.2;
    float sheet = L.g * m.b * 0.85;
    // the paillettes: single screen pixels (R7), 5 % of them, each twinkling at its own phase
    float hc = h21(floor(gl_FragCoord.xy));
    float tw = step(0.95, hc) * max(0.0, sin(uTime * 7.0 + hc * 80.0));
    float sparkle = tw * L.b * m.b * 1.6;
    // the mirror: the foam's light, reflected across the shore, blurred
    float shore = lineAt(uK, img.x).x;
    float below = img.y - shore;
    float ym = 2.0 * shore - img.y;
    float r = 0.0;
    if (m.b > 0.01 && below > 0.0) {
      ${refl}
      r *= ${(1 / offs.length).toFixed(4)} * 0.5 * exp(-below / 45.0);
    }
    float light = (min(glow, 1.3) + sheet + sparkle + r * m.b) * uGain * safeAtt(vUv * 1.0);
    // the lit foam takes the light's colour: its own white turns cyan (an
    // added cyan alone over white foam reads pale blue-white)
    float g1 = min(glow, 1.0) * uGain * safeAtt(vUv);
    c = mix(c, lum(c) * uCol / dot(uCol, vec3(0.2126, 0.7152, 0.0722)), 0.8 * g1);
    vec3 col = c + uCol * light;
    if (uShowMask > 0.5) col = mix(c * 0.5, vec3(m.r * 0.6, m.g, m.b), 0.6);
    gl_FragColor = vec4(col, 1.0);
  }`;
}

export class GlowtideVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    this._showMask = urlParam('glowmask') === '1';
    const w = parseInt(urlParam('glowwave'), 10);
    this._debugLine = Number.isFinite(w) ? w : null;
    this._comfort = new VdjComfort(VDJ_PRESETS.lounge.comfort);
    this._post = { bloomStrength: 0.30, bloomThreshold: 0.80, grain: 0, vignette: 0.25, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('glowprobe') === '1' && typeof window !== 'undefined') window.__glowtide = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    this._meshes = [];
    this._assets = getGlowtide();
    if (this._assets) { this._build(this._assets); return; }
    // not preloaded by this entry point: load now, build when it arrives
    const self = this;
    preloadGlowtide().then(function (ok) {
      if (!self._scene) return;
      if (ok) { self._assets = getGlowtide(); self._build(self._assets); }
      // eslint-disable-next-line no-console
      else console.warn('[glowtide] assets unavailable');
    });
  }

  _build(A) {
    const M = A.manifest, W = M.size[0], H = M.size[1], K = M.lines;
    this._W = W; this._K = K; this._lineData = A.lines;
    // the lines as a float texture, read with NEAREST (V3D cannot filter floats)
    const tex = new Float32Array(W * (K + 2) * 4);
    for (let i = 0; i < W * (K + 2); i++) { tex[i * 4] = A.lines[i * 2]; tex[i * 4 + 1] = A.lines[i * 2 + 1]; tex[i * 4 + 3] = 1; }
    this._linesTex = new THREE.DataTexture(tex, W, K + 2, THREE.RGBAFormat, THREE.FloatType);
    this._linesTex.minFilter = this._linesTex.magFilter = THREE.NearestFilter;
    this._linesTex.generateMipmaps = false; this._linesTex.needsUpdate = true;
    // each line's mean y and how much of the width it covers (its weight)
    this._lineInfo = [];
    for (let k = 0; k < K; k++) { let y = 0, s = 0; for (let x = 0; x < W; x++) { y += this._ly(k, x); s += A.lines[(k * W + x) * 2 + 1]; } this._lineInfo.push({ y: y / W, weight: s / W }); }

    this._crop = new THREE.Vector4(0, 0, W, H);
    const common = { uCrop: { value: this._crop }, uImg: { value: new THREE.Vector2(W, H) }, uLines: { value: this._linesTex }, uK: { value: K } };
    this._LU = Object.assign({}, common);
    for (let i = 0; i < MAX_WAVES; i++) { this._LU['uWA' + i] = { value: new THREE.Vector4() }; this._LU['uWB' + i] = { value: new THREE.Vector4() }; }
    for (let i = 0; i < MAX_SPARKS; i++) this._LU['uSP' + i] = { value: new THREE.Vector4() };
    this._lightMat = new THREE.ShaderMaterial({ uniforms: this._LU, vertexShader: quadVertexShader, fragmentShader: lightFragmentShader(), depthTest: false, depthWrite: false });
    this._lightScene = new THREE.Scene();
    this._quadGeo = new THREE.PlaneGeometry(1, 1);
    const lq = new THREE.Mesh(this._quadGeo, this._lightMat); lq.frustumCulled = false; this._lightScene.add(lq);
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._lightTarget = null;

    this._DU = Object.assign({}, common, {
      uPhoto: { value: A.photo }, uMasks: { value: A.masks }, uLight: { value: null }, uCol: { value: new THREE.Vector3(CYAN[0], CYAN[1], CYAN[2]) },
      uTime: { value: 0 }, uGain: { value: 1 }, uShowMask: { value: this._showMask ? 1 : 0 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    });
    const display = new THREE.Mesh(this._quadGeo, new THREE.ShaderMaterial({ uniforms: this._DU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader(this._layout.taps), depthTest: false, depthWrite: false }));
    display.frustumCulled = false; display.renderOrder = -1000;
    const self = this;
    display.onBeforeRender = function (renderer) { self._passes(renderer); };
    this._scene.add(display);
    this._meshes = [display];

    this._time = 0; this._beats = 0; this._energy = null;
    this._waves = []; this._sparks = [];
    this._phasePhrase = null; this._phrases = 0;
    this._pendingDrop = [];
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._seed = 11;
    this._paletteKey = null;
    this._lastDebugWave = -1e9;
    this._log = [];
    this._applyPalette(null);
  }

  _ly(k, x) { return this._lineData[(k * this._W + Math.max(0, Math.min(this._W - 1, Math.round(x)))) * 2]; }
  _rand() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  // R4 within the brief: cyan, tinted by the palette at 25 % at most, and only
  // toward green or blue (its hue clamped into [0.33, 0.66])
  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : null;
    const key = v ? v.map(String).join('|') : '-';
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    const c = new THREE.Color(CYAN[0], CYAN[1], CYAN[2]);
    if (v) {
      const hsl = { h: 0, s: 0, l: 0 };
      new THREE.Color(v[0]).getHSL(hsl);
      const t = new THREE.Color().setHSL(Math.max(0.33, Math.min(0.66, hsl.h)), 0.9, 0.55);
      const lc = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b, lt = 0.2126 * t.r + 0.7152 * t.g + 0.0722 * t.b;
      t.multiplyScalar(lc / Math.max(lt, 1e-3));            // same luminance as the cyan
      c.lerp(t, 0.25);
    }
    this._DU.uCol.value.set(c.r, c.g, c.b);
  }

  _layoutFor(renderer) {
    const rt = renderer.getRenderTarget();
    const size = rt ? _size.set(rt.width, rt.height) : renderer.getDrawingBufferSize(_size);
    const M = this._assets.manifest;
    const c = cropFor(M.size[0], M.size[1], size.x / Math.max(1, size.y));
    this._crop.set(c[0], c[1], c[2], c[3]);
    const w = Math.max(2, Math.round(size.x * this._layout.light)), h = Math.max(2, Math.round(size.y * this._layout.light));
    if (!this._lightTarget || this._lightTarget.width !== w || this._lightTarget.height !== h) {
      if (this._lightTarget) this._lightTarget.dispose();
      this._lightTarget = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
      this._DU.uLight.value = this._lightTarget.texture;
    }
  }

  _passes(renderer) {
    this._layoutFor(renderer);
    if (this._frozen && this._lightDone) return;            // pause: the light field holds
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this._lightTarget);
    renderer.render(this._lightScene, this._ortho);
    renderer.setRenderTarget(prev);
    this._lightDone = true;
  }

  // A wave breaking along line k, from one side to the other.
  _wave(k, why) {
    if (!this._lineInfo || k < 0 || k >= this._K) return;
    const W = this._W, dir = this._rand() < 0.5 ? 1 : -1;
    const v = W / CROSS_S;
    const intensity = 0.45 + 0.55 * clamp01(this._energy == null ? 0.5 : this._energy);
    const shore = this._lineData[(this._K * W + Math.round(W / 2)) * 2];
    const delay = Math.max(0.2, (shore - this._lineInfo[k].y) / INRUSH_PX_S);
    this._waves.push({ k, x0: dir > 0 ? -60 : W + 60, dir, v, born: this._time, I: intensity, delay, life: (W + 120) / v + delay + SHEET_TAIL_S });
    while (this._waves.length > MAX_WAVES) this._waves.shift();
    this._log.push({ t: +this._time.toFixed(2), what: 'wave', k, why, dir });
  }
  _pickLine() {
    let tot = 0; for (const l of this._lineInfo) tot += l.weight + 0.05;
    let r = this._rand() * tot;
    for (let k = 0; k < this._K; k++) { r -= this._lineInfo[k].weight + 0.05; if (r <= 0) return k; }
    return this._K - 1;
  }
  _spark() {
    const k = this._pickLine(), x = this._rand() * this._W;
    this._sparks.push({ x, y: this._ly(k, x) + (this._rand() - 0.3) * 4, born: this._time });
    while (this._sparks.length > MAX_SPARKS) this._sparks.shift();
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  update(deltaTime, profile) {
    if (!this._assets || !this._LU) return;
    this._applyPalette(profile.artworkPalette);
    this._frozen = profile.playbackState !== 'PLAYING';
    if (this._frozen) return;                               // R11: a freeze, never black
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._time += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const barS = 240 / bpm;
    const director = profile.director;
    const e = director && Number.isFinite(director.intensity) ? clamp01(director.intensity) : 0.5;
    if (this._energy == null) this._energy = e;
    this._energy += (e - this._energy) * (1 - Math.exp(-dt / (ENERGY_BARS * barS)));

    // PHRASE: a wave
    this._beats += dt * bpm / 60;
    const ph = profile.phrasePhase;
    let phrase = false;
    if (ph != null && profile.beatPhase != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (Math.floor(this._beats / 16) !== Math.floor((this._beats - dt * bpm / 60) / 16)) phrase = true;
    // DROP: three waves over 4 bars, each on a line nearer the shore
    let drop = this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    // the drop's three waves are the only ones during its 4 bars
    const inDrop = this._time - this._lastDropAt < DROP_BARS * barS || drop;
    if (phrase) { this._phrases++; if (this._debugLine == null && !inDrop) this._wave(this._pickLine(), 'phrase'); }
    if (drop && this._time - this._lastDropAt > DROP_BARS * barS) {
      this._lastDropAt = this._time;
      const ks = []; for (let k = Math.max(0, this._K - 3); k < this._K; k++) ks.push(k);
      ks.forEach((k, i) => this._pendingDrop.push({ at: this._time + i * DROP_BARS * barS / 3, k }));
      this._log.push({ t: +this._time.toFixed(2), what: 'drop' });
    }
    this._pendingDrop = this._pendingDrop.filter((p) => { if (p.at <= this._time) { this._wave(p.k, 'drop'); return false; } return true; });
    // debug: a wave on line n now and every 10 s
    if (this._debugLine != null && this._time - this._lastDebugWave >= DEBUG_WAVE_EVERY_S) { this._lastDebugWave = this._time; this._wave(Math.min(this._K - 1, Math.max(0, this._debugLine)), 'debug'); }

    // the waves' lives; sparks only between waves
    this._waves = this._waves.filter((w) => this._time - w.born < w.life);
    const busy = this._waves.some((w) => this._time - w.born < (this._W + 120) / w.v + 1.5);
    if (!busy && this._rand() < SPARK_RATE * dt) this._spark();
    this._sparks = this._sparks.filter((s) => this._time - s.born < SPARK_S);

    const U = this._LU;
    for (let i = 0; i < MAX_WAVES; i++) {
      const w = this._waves[i];
      if (!w) { U['uWB' + i].value.set(0, 0, 0, 0); continue; }
      U['uWA' + i].value.set(w.k, w.x0, w.dir, this._time - w.born);
      U['uWB' + i].value.set(w.I, w.v, w.delay, 1);
    }
    for (let i = 0; i < MAX_SPARKS; i++) {
      const s = this._sparks[i];
      if (!s) { U['uSP' + i].value.set(0, 0, 0, 0); continue; }
      const a = (this._time - s.born) / SPARK_S;
      U['uSP' + i].value.set(s.x, s.y, 0, 0.9 * Math.sin(Math.PI * clamp01(a)));
    }
    this._DU.uTime.value = this._time % 1000;
    // R10 (lounge): the mean luminance under 0.35, estimated from the light in play
    let est = 0.06; for (const w of this._waves) est += 0.03 * w.I;
    this._DU.uGain.value = this._comfort.measure(est, dt);
  }

  getProbe() {
    return {
      time: +(this._time || 0).toFixed(2), energy: this._energy, lines: this._lineInfo, crop: this._crop ? this._crop.toArray() : null,
      waves: (this._waves || []).map((w) => ({ k: w.k, age: +(this._time - w.born).toFixed(2), dir: w.dir, I: +w.I.toFixed(2), delay: +w.delay.toFixed(2) })),
      sparks: (this._sparks || []).length, colour: this._DU ? this._DU.uCol.value.toArray().map((v) => +v.toFixed(3)) : null, log: (this._log || []).slice(-10)
    };
  }

  dispose() {
    for (const m of this._meshes || []) { m.onBeforeRender = null; if (this._scene) this._scene.remove(m); m.material.dispose(); }
    if (this._quadGeo) this._quadGeo.dispose();
    if (this._lightMat) this._lightMat.dispose();
    if (this._lightTarget) this._lightTarget.dispose();
    if (this._linesTex) this._linesTex.dispose();
    if (typeof window !== 'undefined' && window.__glowtide === this) window.__glowtide = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
